import React from 'react';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { Avatar, Tooltip } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Appointment } from '../../services/appointmentApi';
import { CapacityIndicator } from './CapacityIndicator';
import { AppointmentCard } from './AppointmentCard';
import './EmployeeColumn.css';

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
}

interface TechnicianSchedule {
  id: number;
  user_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface ScheduleException {
  id: number;
  user_id: number;
  exception_type: 'time_off' | 'break' | 'special_hours' | 'holiday';
  start_datetime: string;
  end_datetime: string;
  reason?: string;
}

interface TimeSlot {
  hour: number;
  minute: number;
  time: string;
}

interface EmployeeColumnProps {
  employee: Employee;
  date: Dayjs;
  appointments: Appointment[];
  timeSlots: TimeSlot[];
  schedule: TechnicianSchedule[];
  exceptions: ScheduleException[];
  onAppointmentClick: (appointment: Appointment) => void;
}

export const EmployeeColumn: React.FC<EmployeeColumnProps> = ({
  employee,
  date,
  appointments,
  timeSlots,
  schedule,
  exceptions,
  onAppointmentClick
}) => {
  const employeeName = `${employee.first_name} ${employee.last_name}`;

  // Get schedule for current day
  const dayOfWeek = date.day(); // 0=Sunday, 1=Monday, etc.
  const todaySchedule = schedule.find(s => s.day_of_week === dayOfWeek && s.is_active);

  // Check if time slot is available (within working hours and no exceptions)
  const isTimeSlotAvailable = (hour: number, minute: number): boolean => {
    // If no schedule, employee is not available
    if (!todaySchedule) return false;

    // Check if time falls within working hours
    const slotTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    if (slotTime < todaySchedule.start_time || slotTime >= todaySchedule.end_time) {
      return false;
    }

    // Check if there's an exception at this time
    const slotDateTime = date.hour(hour).minute(minute);
    for (const exception of exceptions) {
      const exceptionStart = dayjs(exception.start_datetime);
      const exceptionEnd = dayjs(exception.end_datetime);

      // Check if slot time is within exception range (start <= slot < end)
      if ((slotDateTime.isAfter(exceptionStart) || slotDateTime.isSame(exceptionStart)) && slotDateTime.isBefore(exceptionEnd)) {
        return false; // Time slot blocked by exception
      }
    }

    return true;
  };

  // Calculate capacity (simple version - will be enhanced with backend data)
  const calculateCapacity = (): number => {
    // Total available minutes in a day (7 AM - 7 PM = 12 hours = 720 minutes)
    const totalMinutes = 12 * 60;

    // Calculate total booked minutes
    const bookedMinutes = appointments.reduce((total, apt) => {
      const start = dayjs(apt.start_time);
      const end = dayjs(apt.end_time);

      // Only count if appointment is on this date
      if (!start.isSame(date, 'day')) return total;

      return total + end.diff(start, 'minutes');
    }, 0);

    return (bookedMinutes / totalMinutes) * 100;
  };

  const capacity = calculateCapacity();

  // Position appointments on the timeline
  // Each 15-minute slot = 80px, so 1 minute = 80/15 = 5.33px
  const PIXELS_PER_MINUTE = 80 / 15;

  const positionedAppointments = appointments.map(apt => {
    const start = dayjs(apt.start_time);
    const end = dayjs(apt.end_time);

    // Calculate position and height in pixels
    const startHour = start.hour();
    const startMinute = start.minute();

    // Position from top (7 AM = 0px)
    const minutesFrom7AM = (startHour - 7) * 60 + startMinute;
    const topPosition = minutesFrom7AM * PIXELS_PER_MINUTE;

    // Height based on duration (in pixels)
    const durationMinutes = end.diff(start, 'minutes');
    const height = durationMinutes * PIXELS_PER_MINUTE;

    return {
      appointment: apt,
      topPosition,
      height
    };
  });

  return (
    <div className="employee-column">
      {/* Header */}
      <div className="employee-column-header">
        <div className="employee-info">
          <Avatar
            size="default"
            icon={<UserOutlined />}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.25)',
              color: 'white',
              border: '2px solid white',
              fontWeight: 'bold'
            }}
          />
          <Tooltip title={`${employeeName} (${employee.role})`}>
            <div className="employee-name">{employeeName}</div>
          </Tooltip>
        </div>
        <CapacityIndicator capacity={capacity} whiteText={true} />
      </div>

      {/* Time slots grid */}
      <div className="employee-timeline">
        {timeSlots.map((slot, index) => {
          const isAvailable = isTimeSlotAvailable(slot.hour, slot.minute);
          return (
            <div
              key={index}
              className={`employee-time-slot ${slot.minute === 0 ? 'hour-mark' : ''} ${!isAvailable ? 'unavailable' : ''}`}
              data-time={slot.time}
              title={!isAvailable ? 'Employee not available' : ''}
            >
              {/* This will be droppable zone in Phase 2 */}
            </div>
          );
        })}

        {/* Positioned appointments */}
        <div className="appointments-container">
          {positionedAppointments.map(({ appointment, topPosition, height }) => (
            <div
              key={appointment.id}
              className="appointment-wrapper"
              style={{
                top: `${topPosition}px`,
                height: `${height}px`
              }}
            >
              <AppointmentCard
                appointment={appointment}
                onClick={() => onAppointmentClick(appointment)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
